import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../utils/apiClient';

const PAGE_SIZE = 20;
/** Сервер отклоняет запросы короче двух символов — не дёргаем его зря. */
const MIN_QUERY_LENGTH = 2;

/**
 * Поиск по тексту переписки.
 *
 * Курсор здесь — позиция СКАНИРОВАНИЯ, а не последнее совпадение: текст
 * сообщений зашифрован, поэтому сервер перебирает их пачками и расшифровывает
 * на ходу. Из-за этого страница может вернуться пустой, но с курсором — это не
 * «конец списка», а «в просмотренном куске совпадений не было». Конец
 * обозначает только `nextCursor === null`.
 *
 * @param {string} query - поисковый запрос (уже с задержкой ввода)
 * @param {boolean} enabled - искать ли (вкладка «Сообщения» открыта)
 */
export function useMessageSearch(query = '', enabled = true) {
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    const trimmed = query.trim();
    const active = enabled && trimmed.length >= MIN_QUERY_LENGTH;

    // Ответы обесценившихся запросов (запрос успели сменить) выбрасываем:
    // иначе выдача мигала бы результатами предыдущего слова.
    const requestRef = useRef(0);

    const fetchPage = useCallback(async (from) => {
        const requestId = ++requestRef.current;

        try {
            const url = new URL('/api/messages/search', process.env.REACT_APP_API_URL);
            url.searchParams.append('q', trimmed);
            url.searchParams.append('limit', String(PAGE_SIZE));
            if (from) url.searchParams.append('cursor', String(from));

            const res = await apiFetch(url.toString(), { method: 'GET' });
            const data = await res.json();

            if (!res.ok) throw new Error(data?.error ?? 'Ошибка сервера');
            if (requestId !== requestRef.current) return;

            const nextCursor = data?.nextCursor ?? null;
            setItems(prev => from ? [...prev, ...(data?.items ?? [])] : (data?.items ?? []));
            setCursor(nextCursor);
            setHasMore(Boolean(nextCursor));
            setError(null);
        } catch (err) {
            if (requestId !== requestRef.current) return;
            setError(err.message);
            setHasMore(false);
        }
    }, [trimmed]);

    useEffect(() => {
        if (!active) {
            requestRef.current += 1;
            setItems([]);
            setCursor(null);
            setHasMore(false);
            setError(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        fetchPage(null).finally(() => setLoading(false));
    }, [active, fetchPage]);

    const loadMore = useCallback(async () => {
        if (!active || !hasMore || loading || loadingMore) return;

        setLoadingMore(true);
        await fetchPage(cursor);
        setLoadingMore(false);
    }, [active, hasMore, loading, loadingMore, cursor, fetchPage]);

    return { items, loading, loadingMore, hasMore, loadMore, error };
}
